import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { useVisits } from '@/hooks/use-visits';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/use-auth';
import { customerService, productService } from '@/lib/packages';
import { useToast } from '@/hooks/use-toast';
import { CustomerRow, ProductRow } from '@/integrations/supabase/types';
import { VisitPhotoService } from '@/lib/storage';
import { 
  Search,
  Package,
  User,
  Users,
  Plus,
  Loader2,
  Camera,
  Calendar as CalendarIcon,
  Clock,
  FileText,
  AlertCircle,
  UserPlus,
  Upload,
  X,
  Image
} from 'lucide-react';

interface Customer {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
}

interface Product {
  id: string;
  name: string;
  price: number;
  category: string;
  item_code: string;
}

interface NewVisitModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preselectedProduct?: ProductRow;
  preselectedCustomer?: CustomerRow;
}

const visitTypes = [
  { value: 'inceledi', label: 'Ürünü İnceledi', color: 'bg-blue-500/20 text-blue-600' },
  { value: 'fiyat_talep', label: 'Fiyat Talep Etti', color: 'bg-yellow-500/20 text-yellow-600' },
  { value: 'teklif_verildi', label: 'Teklif Verildi', color: 'bg-orange-500/20 text-orange-600' },
  { value: 'satis_yapildi', label: 'Satış Yapıldı', color: 'bg-green-500/20 text-green-600' },
];


export const NewVisitModal = ({ 
  open, 
  onOpenChange, 
  preselectedProduct,
  preselectedCustomer 
}: NewVisitModalProps) => {
  const navigate = useNavigate();
  const { startVisit, isStartingVisit } = useVisits();
  const { companyId } = useAuth();
  const { toast } = useToast();
  
  // Form states
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerRow | null>(preselectedCustomer || null);
  const [selectedProduct, setSelectedProduct] = useState<ProductRow | null>(preselectedProduct || null);
  const [visitType, setVisitType] = useState('');
  const [visitNotes, setVisitNotes] = useState('');
  const [visitLocation, setVisitLocation] = useState('');
  const [photos, setPhotos] = useState<string[]>([]);
  const [tempPhotoPaths, setTempPhotoPaths] = useState<string[]>([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ uploaded: 0, total: 0 });
  const [reminderDate, setReminderDate] = useState<Date>();
  const [reminderTime, setReminderTime] = useState('');
  const [reminderNote, setReminderNote] = useState('');
  
  // Search states
  const [customerSearch, setCustomerSearch] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [showCustomerResults, setShowCustomerResults] = useState(false);
  const [showProductResults, setShowProductResults] = useState(false);
  
  // Database states
  const [customers, setCustomers] = useState<CustomerRow[]>([]);
  const [products, setProducts] = useState<ProductRow[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);

  const getCustomerName = (customer: CustomerRow) => {
    return `${customer.first_name || ''} ${customer.last_name || ''}`.trim() || 'Adsız Müşteri';
  };

  // Debounced search for customers
  const searchCustomers = useCallback(async (searchTerm: string) => {
    if (!companyId || searchTerm.length < 2) {
      setCustomers([]);
      return;
    }

    setLoadingCustomers(true);
    try {
      const results = await customerService.searchCustomers(companyId, searchTerm, 20);
      setCustomers(results);
    } catch (error) {
      console.error('Error searching customers:', error);
      toast({
        title: "Hata",
        description: "Müşteri arama sırasında hata oluştu",
        variant: "destructive",
      });
    } finally {
      setLoadingCustomers(false);
    }
  }, [companyId, toast]);

  // Debounced search for products
  const searchProducts = useCallback(async (searchTerm: string) => {
    if (!companyId || searchTerm.length < 2) {
      setProducts([]);
      return;
    }

    setLoadingProducts(true);
    try {
      const results = await productService.searchProducts(companyId, searchTerm, 20);
      setProducts(results);
    } catch (error) {
      console.error('Error searching products:', error);
      toast({
        title: "Hata",
        description: "Ürün arama sırasında hata oluştu",
        variant: "destructive",
      });
    } finally {
      setLoadingProducts(false);
    }
  }, [companyId, toast]);

  // Effect for customer search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (customerSearch) {
        searchCustomers(customerSearch);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [customerSearch, searchCustomers]);

  // Effect for product search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (productSearch) {
        searchProducts(productSearch);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [productSearch, searchProducts]);

  const handleCustomerSelect = (customer: CustomerRow) => {
    setSelectedCustomer(customer);
    setCustomerSearch(getCustomerName(customer));
    setShowCustomerResults(false);
    setCustomers([]);
  };

  const handleProductSelect = (product: ProductRow) => {
    setSelectedProduct(product);
    setProductSearch(product.name);
    setShowProductResults(false);
    setProducts([]);
  };

  const handleTemporaryVisit = async () => {
    try {
      const currentTime = new Date().toISOString();
      const visitData = {
        customer_id: null, // Explicitly set to null for temporary visits
        started_at: currentTime,
        ended_at: currentTime, // Set same time for manual visits
        source: 'manual',
        temporary: true, // Force temporary to true
        visit_type: visitType,
        product_id: selectedProduct?.id,
        notes: visitNotes,
        location: visitLocation,
        photos,
        reminder_date: reminderDate?.toISOString(),
        reminder_time: reminderTime,
        reminder_note: reminderNote,
      };

      startVisit(visitData, {
        onSuccess: async (newVisit) => {
          // Move temp photos to visit folder if any were uploaded
          if (tempPhotoPaths.length > 0 && companyId) {
            try {
              const finalPhotoPaths = await VisitPhotoService.moveTempPhotosToVisit(
                companyId,
                tempPhotoPaths,
                newVisit.id
              );
              console.log('Photos moved to visit folder:', finalPhotoPaths);
            } catch (error) {
              console.error('Error moving photos to visit folder:', error);
              // Don't fail the visit creation for photo move errors
            }
          }
          
          toast({
            title: "Geçici ziyaret kaydedildi",
            description: "Ziyaret geçici olarak kaydedildi. Daha sonra müşteri ile eşleştirebilirsiniz.",
          });
          
          navigate(`/visits/${newVisit.id}`);
          onOpenChange(false);
          resetForm();
        }
      });
    } catch (error) {
      console.error('Error creating temporary visit:', error);
    }
  };

  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0 || !companyId) return;

    setUploadingPhotos(true);
    setUploadProgress({ uploaded: 0, total: files.length });

    try {
      const results = await VisitPhotoService.uploadMultiplePhotos(
        companyId,
        files,
        undefined, // No visit ID yet, upload to temp
        (uploaded, total) => {
          setUploadProgress({ uploaded, total });
        }
      );

      const successfulUploads = results.filter(result => result.success);
      const failedUploads = results.filter(result => !result.success);

      if (successfulUploads.length > 0) {
        const newPhotoUrls = successfulUploads.map(result => result.url);
        const newPhotoPaths = successfulUploads.map(result => result.path);
        
        setPhotos(prev => [...prev, ...newPhotoUrls]);
        setTempPhotoPaths(prev => [...prev, ...newPhotoPaths]);

        toast({
          title: "Fotoğraflar yüklendi",
          description: `${successfulUploads.length} fotoğraf başarıyla yüklendi.`,
        });
      }

      if (failedUploads.length > 0) {
        toast({
          title: "Bazı fotoğraflar yüklenemedi",
          description: `${failedUploads.length} fotoğraf yüklenirken hata oluştu.`,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Photo upload error:', error);
      toast({
        title: "Hata",
        description: "Fotoğraflar yüklenirken hata oluştu",
        variant: "destructive",
      });
    } finally {
      setUploadingPhotos(false);
      setUploadProgress({ uploaded: 0, total: 0 });
      // Reset the file input
      event.target.value = '';
    }
  };

  const handleRemovePhoto = async (index: number) => {
    const photoToRemove = photos[index];
    const pathToRemove = tempPhotoPaths[index];

    // Remove from storage if it's a temp upload
    if (pathToRemove) {
      try {
        await VisitPhotoService.deletePhoto(pathToRemove);
      } catch (error) {
        console.error('Error deleting photo:', error);
      }
    }

    // Update state
    setPhotos(prev => prev.filter((_, i) => i !== index));
    setTempPhotoPaths(prev => prev.filter((_, i) => i !== index));
  };

  const handleCreateVisit = async () => {
    try {
      const currentTime = new Date().toISOString();
      const visitData = {
        customer_id: selectedCustomer?.id || null,
        started_at: currentTime,
        ended_at: currentTime, // Set same time for manual visits
        source: 'manual',
        temporary: !selectedCustomer,
        visit_type: visitType,
        product_id: selectedProduct?.id,
        notes: visitNotes,
        location: visitLocation,
        photos,
        reminder_date: reminderDate?.toISOString(),
        reminder_time: reminderTime,
        reminder_note: reminderNote,
      };

      startVisit(visitData, {
        onSuccess: async (newVisit) => {
          // Move temp photos to visit folder if any were uploaded
          if (tempPhotoPaths.length > 0 && companyId) {
            try {
              const finalPhotoPaths = await VisitPhotoService.moveTempPhotosToVisit(
                companyId,
                tempPhotoPaths,
                newVisit.id
              );
              console.log('Photos moved to visit folder:', finalPhotoPaths);
            } catch (error) {
              console.error('Error moving photos to visit folder:', error);
              // Don't fail the visit creation for photo move errors
            }
          }
          
          navigate(`/visits/${newVisit.id}`);
          onOpenChange(false);
          resetForm();
        }
      });
    } catch (error) {
      console.error('Error creating visit:', error);
    }
  };

  const resetForm = () => {
    setSelectedCustomer(preselectedCustomer || null);
    setSelectedProduct(preselectedProduct || null);
    setVisitType('');
    setVisitNotes('');
    setVisitLocation('');
    setPhotos([]);
    setTempPhotoPaths([]);
    setUploadingPhotos(false);
    setUploadProgress({ uploaded: 0, total: 0 });
    setReminderDate(undefined);
    setReminderTime('');
    setReminderNote('');
    setCustomerSearch('');
    setProductSearch('');
    setShowCustomerResults(false);
    setShowProductResults(false);
    setCustomers([]);
    setProducts([]);
  };

  const canCreateVisit = () => {
    return visitType && (selectedCustomer || customerSearch === ''); // Allow temporary visits
  };

  const canCreateTemporaryVisit = () => {
    return visitType; // Only visit type is required for temporary visits
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Yeni Ziyaret Kaydı</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Customer Selection */}
          <div className="space-y-3">
            <Label>Müşteri Seç</Label>
            <div className="relative">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Müşteri ara..."
                    value={customerSearch}
                    onChange={(e) => {
                      setCustomerSearch(e.target.value);
                      setShowCustomerResults(e.target.value.length > 0);
                      if (e.target.value === '') {
                        setSelectedCustomer(null);
                        setCustomers([]);
                      }
                    }}
                    onFocus={() => setShowCustomerResults(customerSearch.length > 0)}
                    onBlur={() => setTimeout(() => setShowCustomerResults(false), 150)}
                    className="pl-10"
                  />
                  
                  {showCustomerResults && (
                    <div className="absolute top-full mt-1 w-full bg-popover border border-border rounded-md shadow-lg z-10 max-h-48 overflow-y-auto backdrop-blur-sm">
                      {loadingCustomers ? (
                        <div className="p-3 text-muted-foreground text-center">
                          <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />
                          Müşteriler aranıyor...
                        </div>
                      ) : customers.length > 0 ? (
                        customers.map((customer) => (
                          <div
                            key={customer.id}
                            className="flex items-center gap-3 p-3 hover:bg-accent cursor-pointer"
                            onClick={() => handleCustomerSelect(customer)}
                          >
                            <Avatar className="w-8 h-8">
                              <AvatarFallback className="bg-accent/20 text-accent text-xs">
                                {getCustomerName(customer).split(' ').map(n => n[0]).join('')}
                              </AvatarFallback>
                            </Avatar>
                            <div>
                              <p className="font-medium">{getCustomerName(customer)}</p>
                              <p className="text-sm text-muted-foreground">{customer.email}</p>
                            </div>
                          </div>
                        ))
                      ) : customerSearch.length >= 2 ? (
                        <div className="p-3 text-muted-foreground text-center">
                          Müşteri bulunamadı
                        </div>
                      ) : (
                        <div className="p-3 text-muted-foreground text-center">
                          En az 2 karakter girin
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                <Button 
                  variant="outline" 
                  onClick={handleTemporaryVisit}
                  disabled={!canCreateTemporaryVisit() || isStartingVisit}
                  className="gap-2"
                >
                  {isStartingVisit ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <UserPlus className="w-4 h-4" />
                  )}
                  Geçici Kayıt
                </Button>
              </div>
              
              <p className="text-xs text-muted-foreground mt-1">
                Daha sonra doldurulacak kayıtlar için "Geçici Kayıt" seçeneğini kullanın
              </p>
            </div>

            {selectedCustomer && (
              <Card className="bg-accent/5">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Avatar className="w-10 h-10">
                      <AvatarFallback className="bg-accent/20 text-accent">
                        {getCustomerName(selectedCustomer).split(' ').map(n => n[0]).join('')}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <h4 className="font-medium">{getCustomerName(selectedCustomer)}</h4>
                      <p className="text-sm text-muted-foreground">{selectedCustomer.email}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      onClick={() => {
                        setSelectedCustomer(null);
                        setCustomerSearch('');
                        setCustomers([]);
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {!selectedCustomer && customerSearch === '' && (
              <Card className="bg-warning/5 border-warning/20">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-warning" />
                    <div>
                      <h4 className="font-medium text-warning">Geçici Ziyaret Kaydı</h4>
                      <p className="text-sm text-muted-foreground">
                        Bu kayıt daha sonra müşteri ile eşleştirilebilir
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          <Separator />

          {/* Product Selection (Optional) */}
          <div className="space-y-3">
            <Label>Ürün Seç <span className="text-muted-foreground">(İsteğe bağlı)</span></Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Ürün ara..."
                value={productSearch}
                onChange={(e) => {
                  setProductSearch(e.target.value);
                  setShowProductResults(e.target.value.length > 0);
                  if (e.target.value === '') {
                    setSelectedProduct(null);
                    setProducts([]);
                  }
                }}
                onFocus={() => setShowProductResults(productSearch.length > 0)}
                onBlur={() => setTimeout(() => setShowProductResults(false), 150)}
                className="pl-10"
              />
              
              {showProductResults && (
                <div className="absolute top-full mt-1 w-full bg-popover border border-border rounded-md shadow-lg z-10 max-h-48 overflow-y-auto backdrop-blur-sm">
                  {loadingProducts ? (
                    <div className="p-3 text-muted-foreground text-center">
                      <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />
                      Ürünler aranıyor...
                    </div>
                  ) : products.length > 0 ? (
                    products.map((product) => (
                      <div
                        key={product.id}
                        className="flex items-center gap-3 p-3 hover:bg-accent cursor-pointer"
                        onClick={() => handleProductSelect(product)}
                      >
                        <Package className="w-8 h-8 text-muted-foreground" />
                        <div>
                          <p className="font-medium">{product.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {product.item_code} • ₺{product.price.toLocaleString('tr-TR')}
                          </p>
                        </div>
                      </div>
                    ))
                  ) : productSearch.length >= 2 ? (
                    <div className="p-3 text-muted-foreground text-center">
                      Ürün bulunamadı
                    </div>
                  ) : (
                    <div className="p-3 text-muted-foreground text-center">
                      En az 2 karakter girin
                    </div>
                  )}
                </div>
              )}
            </div>

            {selectedProduct && (
              <Card className="bg-accent/5">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Package className="w-10 h-10 text-muted-foreground" />
                    <div className="flex-1">
                      <h4 className="font-medium">{selectedProduct.name}</h4>
                      <p className="text-sm text-muted-foreground">
                        {selectedProduct.item_code} • ₺{selectedProduct.price.toLocaleString('tr-TR')}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      onClick={() => {
                        setSelectedProduct(null);
                        setProductSearch('');
                        setProducts([]);
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          <Separator />

          {/* Visit Location */}
          <div className="space-y-3">
            <Label>Konum <span className="text-muted-foreground">(İsteğe bağlı)</span></Label>
            <Input
              placeholder="Örn: Mağaza, Ofis, Müşteri Evi, vs."
              value={visitLocation}
              onChange={(e) => setVisitLocation(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Ziyaretin gerçekleştiği yeri belirtebilirsiniz
            </p>
          </div>

          <Separator />

          {/* Visit Type */}
          <div className="space-y-3">
            <Label>Ziyaret Türü</Label>
            <Select value={visitType} onValueChange={setVisitType}>
              <SelectTrigger>
                <SelectValue placeholder="Ziyaret türünü seçin..." />
              </SelectTrigger>
              <SelectContent>
                {visitTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${type.color.split(' ')[0]}`} />
                      {type.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Photos */}
          <div className="space-y-3">
            <Label>Fotoğraf Ekle <span className="text-muted-foreground">(İsteğe bağlı)</span></Label>
            
            {/* Upload Button */}
            <div className="space-y-3">
              <input
                type="file"
                id="photo-upload"
                multiple
                accept="image/*"
                onChange={handlePhotoUpload}
                className="hidden"
                disabled={uploadingPhotos}
              />
              
              <Button 
                variant="outline" 
                className="w-full gap-2" 
                onClick={() => document.getElementById('photo-upload')?.click()}
                disabled={uploadingPhotos}
              >
                {uploadingPhotos ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Yükleniyor... ({uploadProgress.uploaded}/{uploadProgress.total})
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Fotoğraf Yükle
                  </>
                )}
              </Button>
              
              {/* Photo Preview Grid */}
              {photos.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {photos.map((photoUrl, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={photoUrl}
                        alt={`Ziyaret fotoğrafı ${index + 1}`}
                        className="w-full h-20 object-cover rounded-md border"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemovePhoto(index)}
                        className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              <p className="text-xs text-muted-foreground">
                JPG, PNG veya WebP formatında fotoğraflar yükleyebilirsiniz. Maksimum dosya boyutu: 5MB
              </p>
            </div>
          </div>

          {/* Reminder */}
          <div className="space-y-3">
            <Label>Hatırlatıcı Ekle <span className="text-muted-foreground">(İsteğe bağlı)</span></Label>
            
            {/* Quick Date Presets */}
            <div className="space-y-2">
              <Label className="text-sm text-muted-foreground">Hızlı Seçenekler</Label>
              <div className="grid grid-cols-4 gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  type="button"
                  onClick={() => {
                    const tomorrow = new Date();
                    tomorrow.setDate(tomorrow.getDate() + 1);
                    setReminderDate(tomorrow);
                    setReminderTime('09:00');
                  }}
                  className="text-xs"
                >
                  1 Gün Sonra
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  type="button"
                  onClick={() => {
                    const dayAfter = new Date();
                    dayAfter.setDate(dayAfter.getDate() + 2);
                    setReminderDate(dayAfter);
                    setReminderTime('09:00');
                  }}
                  className="text-xs"
                >
                  2 Gün Sonra
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  type="button"
                  onClick={() => {
                    const threeDays = new Date();
                    threeDays.setDate(threeDays.getDate() + 3);
                    setReminderDate(threeDays);
                    setReminderTime('09:00');
                  }}
                  className="text-xs"
                >
                  3 Gün Sonra
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  type="button"
                  onClick={() => {
                    const oneWeek = new Date();
                    oneWeek.setDate(oneWeek.getDate() + 7);
                    setReminderDate(oneWeek);
                    setReminderTime('09:00');
                  }}
                  className="text-xs"
                >
                  1 Hafta Sonra
                </Button>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Tarih</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {reminderDate ? format(reminderDate, "PPP", { locale: tr }) : "Tarih seç"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={reminderDate}
                      onSelect={setReminderDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              
              <div>
                <Label className="text-sm">Saat</Label>
                <Input
                  type="time"
                  value={reminderTime}
                  onChange={(e) => setReminderTime(e.target.value)}
                />
              </div>
            </div>
            
            {(reminderDate || reminderTime) && (
              <div className="space-y-3">
                <div>
                  <Label className="text-sm">Hatırlatıcı Notu</Label>
                  <Input
                    placeholder="Hatırlatıcı notu..."
                    value={reminderNote}
                    onChange={(e) => setReminderNote(e.target.value)}
                  />
                </div>
                
                <Button
                  variant="ghost"
                  size="sm"
                  type="button"
                  onClick={() => {
                    setReminderDate(undefined);
                    setReminderTime('');
                    setReminderNote('');
                  }}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  Hatırlatıcıyı Temizle
                </Button>
              </div>
            )}
          </div>

          {/* Visit Notes */}
          <div className="space-y-3">
            <Label>Ziyaret Notları</Label>
            <Textarea
              placeholder="Ziyaret detayları, müşteri yorumları, özel notlar..."
              value={visitNotes}
              onChange={(e) => setVisitNotes(e.target.value)}
              rows={4}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button 
              variant="outline" 
              onClick={() => onOpenChange(false)}
              className="flex-1"
            >
              İptal
            </Button>
            <Button 
              onClick={handleCreateVisit}
              disabled={!canCreateVisit() || isStartingVisit}
              className="flex-1 gap-2"
            >
              {isStartingVisit ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Plus className="w-4 h-4" />
              )}
              {isStartingVisit ? 'Oluşturuluyor...' : 'Ziyaret Kaydı Oluştur'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
